import React, {useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {Callout, DirectionalHint, Icon, ITextField, ITextFieldProps, Label, TextField, TooltipHost} from '@fluentui/react';
import MarkdownView from 'react-showdown';
import {ITokenSuggestion} from '../Storefront/useOpportunityTokens';
import TokenSuggestionList from './TokenSuggestionList';
import {SettingId} from '../../settings/registry';

// A single-line/multiline text field that suggests replacement tokens ({{{token}}}) as the admin types. When the
// text immediately left of the caret is an in-progress token — one or more '{' optionally followed by a partial
// identifier — a callout lists matching tokens; picking one splices the full {{{token}}} in at the caret.
//
// Commit semantics mirror ExtensionDataForm's DataField: with updateOnDisengage the value is committed on blur,
// otherwise on every keystroke. Suggestion picks follow the same rule (a pick is just an edit), and use
// mousedown+preventDefault so clicking the callout never blurs the field and never fires a premature commit.

// 1–3 opening braces then the partial token identifier (word chars, ':' and '.' as used by quote_request tokens).
const TRIGGER = /\{{1,3}([\w.:]*)$/;
// Cap generously and let the callout scroll — a small cap would hide the question tokens behind the general ones
// (they trail the list), which is exactly what we don't want.
const MAX_SUGGESTIONS = 50;

interface ITokenAutosuggestFieldProps {
    /** Deep-link identifier; marks the root so a ?focus= link can find this field. */
    settingId?: SettingId;
    label?: string;
    value: string;
    onChange: (value: string) => void;
    suggestions: ITokenSuggestion[];
    multiline?: boolean;
    rows?: number;
    required?: boolean;
    placeholder?: string;
    updateOnDisengage?: boolean;
    toolTip?: string;
    onGetErrorMessage?: (value: string) => string;
    /** Rendered beside the label, as DataField does — for per-field actions like "send a test". */
    labelActions?: React.ReactNode;
    /** Passed to the TextField, for hosts that need their own sizing (e.g. a full-height editor). */
    styles?: ITextFieldProps['styles'];
}

const TokenAutosuggestField: React.FC<ITokenAutosuggestFieldProps> = (props) => {
    const [currentValue, setCurrentValue] = useState<string>(props.value);
    const [calloutOpen, setCalloutOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [triggerStart, setTriggerStart] = useState<number | null>(null);
    const [highlight, setHighlight] = useState(0);

    const containerRef = useRef<HTMLDivElement>(null);
    const fieldRef = useRef<ITextField>(null);
    const pendingCaret = useRef<number | null>(null);

    useEffect(() => {
        if (currentValue !== props.value) {
            setCurrentValue(props.value);
        }
    }, [props.value]);

    // Restore the caret after a token insert re-renders the controlled field.
    useLayoutEffect(() => {
        if (pendingCaret.current != null) {
            fieldRef.current?.setSelectionRange(pendingCaret.current, pendingCaret.current);
            pendingCaret.current = null;
        }
    });

    const matches = useMemo<ITokenSuggestion[]>(() => {
        if (!calloutOpen) return [];
        const q = query.toLowerCase();
        const stripped = (t: string) => t.replace(/[{}]/g, '').toLowerCase();
        return props.suggestions
            .filter(s => q === '' || stripped(s.token).includes(q) || s.description.toLowerCase().includes(q))
            .slice(0, MAX_SUGGESTIONS);
    }, [calloutOpen, query, props.suggestions]);

    const closeCallout = () => {
        setCalloutOpen(false);
        setTriggerStart(null);
    };

    // Detect an in-progress token immediately left of the caret and open/close the callout accordingly.
    const refreshTrigger = (value: string, caret: number) => {
        const match = value.slice(0, caret).match(TRIGGER);
        if (match && props.suggestions.length > 0) {
            setTriggerStart(caret - match[0].length);
            setQuery(match[1]);
            setHighlight(0);
            setCalloutOpen(true);
        } else {
            closeCallout();
        }
    };

    const handleChange = (event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
        const value = newValue ?? '';
        setCurrentValue(value);
        if (!props.updateOnDisengage) props.onChange(value);
        const caret = (event.target as HTMLInputElement | HTMLTextAreaElement).selectionStart ?? value.length;
        refreshTrigger(value, caret);
    };

    const insertToken = (token: string) => {
        if (triggerStart == null) return;
        const caret = fieldRef.current?.selectionStart ?? currentValue.length;
        const next = currentValue.slice(0, triggerStart) + token + currentValue.slice(caret);
        pendingCaret.current = triggerStart + token.length;
        setCurrentValue(next);
        if (!props.updateOnDisengage) props.onChange(next);
        closeCallout();
        fieldRef.current?.focus();
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (!calloutOpen || matches.length === 0) return;
        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setHighlight(h => (h + 1) % matches.length);
                break;
            case 'ArrowUp':
                e.preventDefault();
                setHighlight(h => (h - 1 + matches.length) % matches.length);
                break;
            case 'Enter':
            case 'Tab':
                e.preventDefault();
                insertToken(matches[highlight].token);
                break;
            case 'Escape':
                e.preventDefault();
                closeCallout();
                break;
        }
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (props.updateOnDisengage) props.onChange(e.target.value);
    };

    const renderLabel = () => {
        if (!props.label) return null;
        if (props.toolTip) {
            return (
                <div style={{display: 'inline-flex', alignItems: 'center'}}>
                    <TooltipHost content={<MarkdownView markdown={props.toolTip}/>}>
                        <div style={{display: 'inline-flex'}}>
                            <Label required={props.required}>
                                <Icon iconName={'Info'} style={{paddingRight: '5px'}}/>
                                {props.label}
                            </Label>
                        </div>
                    </TooltipHost>
                    {props.labelActions}
                </div>
            );
        }
        return (
            <div style={{display: 'inline-flex', alignItems: 'center'}}>
                <Label required={props.required}>{props.label}</Label>
                {props.labelActions}
            </div>
        );
    };

    return (
        <div ref={containerRef} data-setting-id={props.settingId}>
            {renderLabel()}
            <TextField
                componentRef={fieldRef}
                value={currentValue}
                type="text"
                multiline={props.multiline}
                rows={props.rows}
                required={props.required}
                placeholder={props.placeholder}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                onBlur={handleBlur}
                onGetErrorMessage={props.onGetErrorMessage}
                // The visible label is rendered separately (to host the tooltip and labelActions), so
                // it isn't associated with the input. Name the control explicitly rather than leave it
                // announced as an unlabelled text box.
                ariaLabel={props.label}
                styles={props.styles ?? {root: {paddingBottom: '2px'}}}
                autoComplete="off"
            />
            {calloutOpen && matches.length > 0 && containerRef.current &&
                <Callout target={containerRef} onDismiss={closeCallout}
                         isBeakVisible={false} gapSpace={2} setInitialFocus={false}
                         directionalHint={DirectionalHint.bottomLeftEdge}
                         styles={{root: {maxWidth: 460, border: '1px solid #edebe9', borderRadius: 4}}}>
                    <TokenSuggestionList matches={matches} highlight={highlight}
                                         onHover={setHighlight} onPick={insertToken} />
                </Callout>}
        </div>
    );
};

export default TokenAutosuggestField;
